const API_BASE =
  process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';

const ACCESS_KEY = 'accessToken';
const REFRESH_KEY = 'refreshToken';

export function getAccessToken() {
  return localStorage.getItem(ACCESS_KEY) || '';
}

export function getRefreshToken() {
  return localStorage.getItem(REFRESH_KEY) || '';
}

export function setTokens({ accessToken, refreshToken }) {
  if (accessToken) localStorage.setItem(ACCESS_KEY, accessToken);
  if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
  // Legacy key used across the app for auth checks
  if (accessToken) localStorage.setItem('ResetToken', accessToken);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem('ResetToken');
}

/** Revokes refresh token on the server, then clears local auth state. */
export async function logout() {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await apiPost('/auth/logout', { refreshToken }, { auth: false });
    } catch {
      // Still clear local session if the network call fails.
    }
  }
  clearTokens();
}

export function apiBaseUrl() {
  return API_BASE;
}

export function wsUrl() {
  const base = API_BASE.replace(/^http/, 'ws');
  const token = getAccessToken();
  return `${base}/ws?access_token=${encodeURIComponent(token)}`;
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return false;
  const res = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!res.ok) return false;
  const pair = await res.json();
  setTokens(pair);
  return true;
}

function buildUrl(path, query) {
  const url = new URL(
    path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`
  );
  if (query) {
    Object.entries(query).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.set(key, String(value));
      }
    });
  }
  return url.toString();
}

export async function apiRequest(path, options = {}, retried = false) {
  const { method = 'GET', body, query, auth = true, headers = {} } = options;
  const reqHeaders = {
    Accept: 'application/json',
    ...headers,
  };
  if (body !== undefined) {
    reqHeaders['Content-Type'] = 'application/json';
  }
  if (auth) {
    const token = getAccessToken();
    if (token) reqHeaders.Authorization = `Bearer ${token}`;
  }
  const res = await fetch(buildUrl(path, query), {
    method,
    headers: reqHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && auth && !retried) {
    const refreshed = await refreshAccessToken();
    if (refreshed) return apiRequest(path, options, true);
  }

  if (res.status === 204) {
    return { ok: res.ok, status: res.status, data: null };
  }

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }

  if (!res.ok) {
    const message =
      data?.error?.message || data?.message || res.statusText || 'Request failed';
    const code = data?.error?.code || 'request_failed';
    const err = new Error(message);
    err.code = code;
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return { ok: true, status: res.status, data };
}

export function apiGet(path, query, options = {}) {
  return apiRequest(path, { ...options, method: 'GET', query });
}

export function apiPost(path, body, options = {}) {
  return apiRequest(path, { ...options, method: 'POST', body });
}

export function apiPut(path, body, options = {}) {
  return apiRequest(path, { ...options, method: 'PUT', body });
}

export function apiPatch(path, body, options = {}) {
  return apiRequest(path, { ...options, method: 'PATCH', body });
}

export function apiDelete(path, options = {}) {
  return apiRequest(path, { ...options, method: 'DELETE' });
}

/** 1-based page for Go API from legacy 0-based pageNumber */
export function pageFromLegacy(pageNumber) {
  return (pageNumber || 0) + 1;
}

export function userTypeFromRoles(roles) {
  if (!roles || !roles.length) return '';
  const hasConsumer = roles.includes('SERVICE_CONSUMER');
  const hasProvider = roles.includes('SERVICE_PROVIDER');
  if (hasConsumer && hasProvider) return 'SERVICE_CONSUMER_PROVIDER';
  if (hasProvider) return 'SERVICE_PROVIDER';
  if (hasConsumer) return 'SERVICE_CONSUMER';
  return roles[0];
}

export function persistUserFromProfile(profile) {
  localStorage.setItem('userId', String(profile.id));
  localStorage.setItem('loginTime', String(new Date().getTime()));
  const userType = userTypeFromRoles(profile.roles);
  if (userType) localStorage.setItem('UserType', userType);
  if (profile.jobTitle) localStorage.setItem('JobTitle', profile.jobTitle);
  localStorage.setItem(
    'userName',
    JSON.stringify({
      fname: profile.firstName,
      mname: profile.middleName || '',
      lname: profile.lastName,
    })
  );
  if (profile.address) {
    localStorage.setItem(
      'location',
      JSON.stringify({
        latitude: profile.address.latitude,
        longitude: profile.address.longitude,
        name: profile.address.line1,
      })
    );
  }
}
